import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  SupportTicketModel,
  SupportTicketSource,
  SupportTicketStatus,
} from '@nx-c4g/c4g-models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { notify } from '../notify-drawer/notify';
import {
  SupportTicketDetailDialogComponent,
} from './support-ticket-detail-dialog.component';
import {
  SupportTicketDialogComponent,
  SupportTicketDialogData,
} from './support-ticket-dialog.component';
import { SupportTicketService } from './support-ticket.service';

@Component({
  selector: 'nx-c4g-support-ticket-hub',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    TranslateModule,
    SupportTicketDialogComponent,
    SupportTicketDetailDialogComponent,
  ],
  templateUrl: './support-ticket-hub.component.html',
  styleUrls: ['./support-ticket-hub.component.scss'],
})
export class SupportTicketHubComponent implements OnInit {
  /** App/source used for new tickets and for filtering “My tickets”. */
  @Input({ required: true }) source!: SupportTicketSource;

  items: SupportTicketModel[] = [];
  total = 0;
  page = 1;
  limit = 10;
  listLoading = false;

  constructor(
    private readonly supportTicketService: SupportTicketService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    void this.loadTickets();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  async openNewTicketDialog(): Promise<void> {
    const ref = this.dialog.open(SupportTicketDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: { source: this.source } as SupportTicketDialogData,
    });
    const submitted = await firstValueFrom(ref.afterClosed());
    if (submitted) {
      this.page = 1;
      await this.loadTickets();
    }
  }

  openTicketDetail(ticket: SupportTicketModel): void {
    this.dialog.open(SupportTicketDetailDialogComponent, {
      data: { ticket },
      width: 'min(560px, 92vw)',
      maxWidth: '92vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  async loadTickets(): Promise<void> {
    this.listLoading = true;
    try {
      const res = await this.supportTicketService.listMine({
        source: this.source,
        page: this.page,
        limit: this.limit,
      });
      this.items = res.items;
      this.total = res.total;
    } catch {
      notify(
        this.translate.instant('supportTickets.toast.loadFailed'),
        'error',
        4000
      );
    } finally {
      this.listLoading = false;
    }
  }

  async prevPage(): Promise<void> {
    if (this.page <= 1) return;
    this.page -= 1;
    await this.loadTickets();
  }

  async nextPage(): Promise<void> {
    if (this.page >= this.totalPages) return;
    this.page += 1;
    await this.loadTickets();
  }

  unreadCount(ticket: SupportTicketModel): number {
    return ticket.unreadMessageCount ?? 0;
  }

  unreadLabel(ticket: SupportTicketModel): string {
    const n = this.unreadCount(ticket);
    return n > 99 ? '99+' : String(n);
  }

  statusLabel(status: SupportTicketStatus): string {
    return this.translate.instant(`supportTickets.status.${status}`);
  }

  formatDate(d: Date | string | undefined): string {
    if (d == null) return '';
    const x = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(x.getTime())) return '';
    const locale = this.translate.currentLang?.startsWith('fr')
      ? 'fr-CA'
      : 'en-CA';
    return x.toLocaleString(locale);
  }
}
